/**
 * BaselineMoodComposer — combines long-term, short-term and instant state
 * into concrete visual parameters. Single source of truth for Buddy appearance.
 * 
 * Key feature: smoothed baseline transitions. When long-term scores change,
 * visual parameters interpolate slowly, creating "muscle memory" in the character.
 * After instant events, Buddy returns to the correct long-term baseline — not neutral.
 */

import { LongTermState, LongTermMoodScores, ShortTermBuddyMood } from '@/lib/types/mood';
import { TransientReactionType } from '@/lib/types/events';
import { SentimentData, DEFAULT_SENTIMENT } from '@/lib/types/sentiment';
import { deriveSentimentInfluence, applySentimentToBaseline } from '@/lib/state/sentimentInfluenceLayer';

// ── Visual parameter set (what Buddy looks like) ──

export interface BaselineVisuals {
  hoverHeight: number;
  floatAmplitude: number;
  pulseRate: number;
  breathingDepth: number;
  blinkInterval: number;
  blinkDuration: number;
  doubleBlink: boolean;
  auraDensity: number;
  auraBrightness: number;
  auraTension: number;
  auraColor: string;
  faceOpenness: number;
  smileBias: number;
  eyeTension: number;
  txBubbleDensity: number;
  txBubbleSpeed: number;
  backgroundStillness: number;
  colorWarmth: number;
  gestureFrequency: number;
  postureBias: number;       // -1 (drooped) to +1 (upright)
  gestureSet: RecurringGesture[];
}

export type RecurringGesture =
  | 'longBlink' | 'softSway' | 'satisfiedBounce'
  | 'doubleBlink' | 'worriedGlance' | 'sigh' | 'tensionPulse'
  | 'confidentLift' | 'glowPulse' | 'smirkHold'
  | 'droop' | 'cautiousBlink' | 'downwardDrift'
  | 'deadpanStare' | 'boredBlink' | 'slowSideSway'
  | 'upwardGlance' | 'nervousWobble' | 'anticipatoryLean' | 'delayedExhale';

// ── Composed output (everything the renderer needs) ──

export interface ComposedBuddyState {
  baseline: BaselineVisuals;
  shortTermMood: ShortTermBuddyMood;
  activeTransient: TransientReactionType | null;
  longTermStatusLabel: string;
}

// ── Mood-specific baseline presets ──

const PRESETS: Record<string, BaselineVisuals> = {
  sustained_calm: {
    hoverHeight: 8, floatAmplitude: 0.6, pulseRate: 4.5, breathingDepth: 0.012,
    blinkInterval: 6000, blinkDuration: 300, doubleBlink: false,
    auraDensity: 0.2, auraBrightness: 0.7, auraTension: 0, auraColor: '--bitcoin',
    faceOpenness: 0.35, smileBias: 0.35, eyeTension: 0, postureBias: 0.5,
    txBubbleDensity: 0.15, txBubbleSpeed: 0.3, backgroundStillness: 0.9, colorWarmth: 0.7,
    gestureFrequency: 12000,
    gestureSet: ['longBlink', 'softSway', 'satisfiedBounce'],
  },
  sustained_congestion: {
    hoverHeight: -6, floatAmplitude: 0.25, pulseRate: 2.2, breathingDepth: 0.008,
    blinkInterval: 2800, blinkDuration: 140, doubleBlink: true,
    auraDensity: 0.85, auraBrightness: 0.45, auraTension: 0.85, auraColor: '--stress',
    faceOpenness: 0.65, smileBias: -0.5, eyeTension: 0.85, postureBias: -0.5,
    txBubbleDensity: 0.85, txBubbleSpeed: 0.7, backgroundStillness: 0.15, colorWarmth: 0.25,
    gestureFrequency: 5000,
    gestureSet: ['doubleBlink', 'worriedGlance', 'sigh', 'tensionPulse'],
  },
  sustained_bullish: {
    hoverHeight: 7, floatAmplitude: 0.55, pulseRate: 2.8, breathingDepth: 0.015,
    blinkInterval: 5000, blinkDuration: 180, doubleBlink: false,
    auraDensity: 0.55, auraBrightness: 0.9, auraTension: 0.15, auraColor: '--bitcoin',
    faceOpenness: 0.7, smileBias: 0.5, eyeTension: 0.05, postureBias: 0.8,
    txBubbleDensity: 0.35, txBubbleSpeed: 0.45, backgroundStillness: 0.6, colorWarmth: 0.95,
    gestureFrequency: 9000,
    gestureSet: ['confidentLift', 'glowPulse', 'smirkHold'],
  },
  sustained_caution: {
    hoverHeight: -4, floatAmplitude: 0.35, pulseRate: 4, breathingDepth: 0.01,
    blinkInterval: 4500, blinkDuration: 350, doubleBlink: false,
    auraDensity: 0.3, auraBrightness: 0.3, auraTension: 0.2, auraColor: '--muted-foreground',
    faceOpenness: 0.4, smileBias: -0.35, eyeTension: 0.4, postureBias: -0.4,
    txBubbleDensity: 0.25, txBubbleSpeed: 0.35, backgroundStillness: 0.7, colorWarmth: 0.3,
    gestureFrequency: 10000,
    gestureSet: ['droop', 'cautiousBlink', 'downwardDrift'],
  },
  sustained_crab: {
    hoverHeight: -1, floatAmplitude: 0.12, pulseRate: 6, breathingDepth: 0.005,
    blinkInterval: 5500, blinkDuration: 400, doubleBlink: false,
    auraDensity: 0.12, auraBrightness: 0.35, auraTension: 0, auraColor: '--muted-foreground',
    faceOpenness: 0.3, smileBias: 0, eyeTension: 0.08, postureBias: -0.1,
    txBubbleDensity: 0.2, txBubbleSpeed: 0.2, backgroundStillness: 0.95, colorWarmth: 0.42,
    gestureFrequency: 14000,
    gestureSet: ['deadpanStare', 'boredBlink', 'slowSideSway'],
  },
  sustained_block_pressure: {
    hoverHeight: -2, floatAmplitude: 0.3, pulseRate: 3, breathingDepth: 0.01,
    blinkInterval: 3500, blinkDuration: 200, doubleBlink: true,
    auraDensity: 0.5, auraBrightness: 0.45, auraTension: 0.55, auraColor: '--warning',
    faceOpenness: 0.6, smileBias: -0.2, eyeTension: 0.55, postureBias: -0.3,
    txBubbleDensity: 0.4, txBubbleSpeed: 0.4, backgroundStillness: 0.35, colorWarmth: 0.45,
    gestureFrequency: 7000,
    gestureSet: ['upwardGlance', 'nervousWobble', 'anticipatoryLean', 'delayedExhale'],
  },
  sustained_smooth_flow: {
    hoverHeight: 4, floatAmplitude: 0.5, pulseRate: 4, breathingDepth: 0.012,
    blinkInterval: 5000, blinkDuration: 250, doubleBlink: false,
    auraDensity: 0.25, auraBrightness: 0.6, auraTension: 0.05, auraColor: '--success',
    faceOpenness: 0.5, smileBias: 0.25, eyeTension: 0.05, postureBias: 0.3,
    txBubbleDensity: 0.3, txBubbleSpeed: 0.35, backgroundStillness: 0.75, colorWarmth: 0.6,
    gestureFrequency: 11000,
    gestureSet: ['softSway', 'longBlink', 'satisfiedBounce'],
  },
};

// Neutral fallback
const NEUTRAL_BASELINE: BaselineVisuals = PRESETS.sustained_calm;

// ── Smoothing ──

/** Alpha for baseline visual interpolation.
 *  Lower = slower transitions. 0.1 means each tick moves 10% toward target. */
const BASELINE_SMOOTH_ALPHA = 0.1;

const NUMERIC_KEYS: (keyof BaselineVisuals)[] = [
  'hoverHeight', 'floatAmplitude', 'pulseRate', 'breathingDepth',
  'blinkInterval', 'blinkDuration',
  'auraDensity', 'auraBrightness', 'auraTension',
  'faceOpenness', 'smileBias', 'eyeTension', 'postureBias',
  'txBubbleDensity', 'txBubbleSpeed', 'backgroundStillness', 'colorWarmth',
  'gestureFrequency',
];

/** Compute raw target baseline from weighted mood scores */
function blendBaselineTarget(scores: LongTermMoodScores): BaselineVisuals {
  const entries = Object.entries(scores) as [string, number][];
  const totalWeight = entries.reduce((sum, [, w]) => sum + Math.max(0, w), 0);
  if (totalWeight < 0.01) return NEUTRAL_BASELINE;

  // Find dominant for non-numeric fields
  const sorted = [...entries].sort((a, b) => b[1] - a[1]);
  const dominantKey = sorted[0][0];
  const dominantPreset = PRESETS[dominantKey] || NEUTRAL_BASELINE;

  const result = { ...dominantPreset };

  // Weighted average for numeric values
  for (const key of NUMERIC_KEYS) {
    let weighted = 0;
    for (const [mood, weight] of entries) {
      const preset = PRESETS[mood];
      if (preset && weight > 0.05) {
        weighted += (preset[key] as number) * weight;
      }
    }
    (result as any)[key] = weighted / totalWeight;
  }

  // Aura color from dominant
  result.auraColor = dominantPreset.auraColor;
  result.doubleBlink = entries.some(([m, w]) => w > 0.3 && PRESETS[m]?.doubleBlink);

  // Merge gesture sets from top 2
  const gestures = new Set<RecurringGesture>();
  for (let i = 0; i < Math.min(2, sorted.length); i++) {
    const preset = PRESETS[sorted[i][0]];
    if (preset && sorted[i][1] > 0.15) {
      preset.gestureSet.forEach(g => gestures.add(g));
    }
  }
  result.gestureSet = Array.from(gestures);

  return result;
}

/** Smoothly interpolate baseline visuals toward target.
 *  This is what creates "memory" — Buddy doesn't snap to new states. */
function smoothBaseline(
  prev: BaselineVisuals,
  target: BaselineVisuals,
  alpha: number,
): BaselineVisuals {
  const result = { ...target };

  for (const key of NUMERIC_KEYS) {
    const prevVal = prev[key] as number;
    const tgtVal = target[key] as number;
    (result as any)[key] = prevVal + alpha * (tgtVal - prevVal);
  }

  // Non-numeric fields take from target directly
  result.auraColor = target.auraColor;
  result.doubleBlink = target.doubleBlink;
  result.gestureSet = target.gestureSet;

  return result;
}

// ── Main composer ──

// Store previous baseline for smoothing (module-level for persistence)
let prevSmoothedBaseline: BaselineVisuals | null = null;

export function composeBuddyState(
  longTerm: LongTermState,
  shortTermMood: ShortTermBuddyMood,
  activeTransient: TransientReactionType | null,
  sentiment: SentimentData = DEFAULT_SENTIMENT,
): ComposedBuddyState {
  const target = blendBaselineTarget(longTerm.scores);

  // Apply sentiment influence via centralized bias layer
  const influence = deriveSentimentInfluence(sentiment);
  const modded = applySentimentToBaseline(target, influence);
  target.smileBias = modded.smileBias;
  target.eyeTension = modded.eyeTension;
  target.postureBias = modded.postureBias;
  target.auraBrightness = modded.auraBrightness;
  target.colorWarmth = modded.colorWarmth;
  target.hoverHeight = modded.hoverHeight;
  target.blinkInterval = modded.blinkInterval;
  target.faceOpenness = modded.faceOpenness;

  // Smooth toward target
  const baseline = prevSmoothedBaseline
    ? smoothBaseline(prevSmoothedBaseline, target, BASELINE_SMOOTH_ALPHA)
    : target;

  // Store for next frame
  prevSmoothedBaseline = baseline;

  return {
    baseline,
    shortTermMood,
    activeTransient,
    longTermStatusLabel: longTerm.statusLabel,
  };
}

/** Reset smoothing state (useful for testing) */
export function resetBaselineSmoothing(): void {
  prevSmoothedBaseline = null;
}
